// The AI photo editor's actions on a generated photo, in the Studio Editor's style (components/common/PhotoActionButtons): Info (the
// photo's details in a popover), Crop (the saved result crop), Custom size, Copy, Edit (opens the photo's edit drawer), Download and
// Print. `layout="column"` = the right sidebar (labelled from `xl`, an icon strip with tooltips below); `layout="bar"` = the phone's
// action bar docked above the bottom tabs (icon over label, swipes if it doesn't fit).
import { useTranslation } from 'react-i18next';
import { IconAdjustmentsHorizontal, IconDimensions, IconDownload, IconInfoCircle, IconPrinter } from '@tabler/icons-react';
import { ActionBtn, BarAction, BarButton, EditToggle, InfoButton, PrintButton, btnIcon, customSizeIcon } from '@/components/common/PhotoActionButtons';

/**
 * @param compact       the icon-only range (labels as tooltips)
 * @param tipSide       tooltip side (beside the photo)
 * @param infoContent   the image-info body for the popover (null hides the button)
 */
export default function ResultActions({
    layout = 'column', compact = false, tipSide = 'left',
    onCrop, cropLoading = false, cropDisabled = false,
    onCustom, customLoading = false,
    onCopy, copying = false,
    editOpen = false, edited = false, onToggleEdit,
    infoContent = null,
    onDownload, downloading = false,
    onPrint,
}) {
    const { t } = useTranslation();
    if (layout === 'bar') {
        return (
            <div className="flex shrink-0 overflow-x-auto border-t border-zinc-200 bg-white [scrollbar-width:none] dark:border-zinc-700 dark:bg-zinc-900">
                {infoContent && (
                    <InfoButton side="top" trigger={<BarButton icon={<IconInfoCircle size={21} />} label={t('imageGeneration.imageInfo')} />}>{infoContent}</InfoButton>
                )}
                <BarAction icon={btnIcon('/images/photo-tools/crop.svg')} label={t('imageGeneration.cropImageBtn')} onClick={onCrop} loading={cropLoading} disabled={cropDisabled} />
                <BarAction icon={<IconDimensions size={21} />} label={t('imageGeneration.customSizeDownload')} onClick={onCustom} loading={customLoading} />
                <BarAction icon={btnIcon('/images/photo-tools/copy.svg')} label={t('imageGeneration.copyPhoto')} onClick={onCopy} loading={copying} />
                <BarAction icon={<IconAdjustmentsHorizontal size={21} className="text-primary" />} label={t('imageGeneration.imageAdjustments')} onClick={onToggleEdit} data-state={editOpen ? 'open' : undefined} dot={edited && !editOpen} />
                <BarAction icon={<IconDownload size={21} />} label={t('imageGeneration.downloadPhoto')} onClick={onDownload} loading={downloading} />
                <BarAction icon={<IconPrinter size={21} />} label={t('common.print')} onClick={onPrint} tone="print" />
            </div>
        );
    }
    return (
        // the right sidebar: a compact strip below `xl`, a labelled column from there
        <div className="flex flex-col items-stretch gap-1 max-xl:rounded-xl max-xl:bg-zinc-100 max-xl:p-1 dark:max-xl:bg-zinc-950 xl:gap-2">
            {/* the photo's details first */}
            {infoContent && (
                <InfoButton compact={compact} tipSide={tipSide}>{infoContent}</InfoButton>
            )}
            <ActionBtn compact={compact} tipSide={tipSide} icon={btnIcon('/images/photo-tools/crop.svg')} label={t('imageGeneration.cropImageBtn')} tooltip={t('imageGeneration.cropTooltip')} onClick={onCrop} loading={cropLoading} disabled={cropDisabled} />
            <ActionBtn compact={compact} tipSide={tipSide} icon={customSizeIcon} label={t('imageGeneration.customSizeDownload')} onClick={onCustom} loading={customLoading} />
            <ActionBtn compact={compact} tipSide={tipSide} icon={btnIcon('/images/photo-tools/copy.svg')} label={t('imageGeneration.copyPhoto')} tooltip={t('imageGeneration.copyTooltip')} onClick={onCopy} loading={copying} />
            {onToggleEdit && <EditToggle compact={compact} tipSide={tipSide} open={editOpen} edited={edited} onClick={onToggleEdit} />}
            {/* download sits right above print — the two ways the finished photo leaves */}
            <ActionBtn compact={compact} tipSide={tipSide} icon={<IconDownload size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />} label={t('imageGeneration.downloadPhoto')} tooltip={t('imageGeneration.downloadTooltip')} onClick={onDownload} loading={downloading} />
            <PrintButton compact={compact} tipSide={tipSide} label={t('imageGeneration.printThisImage')} onClick={onPrint} />
        </div>
    );
}
